/**
 * The store's words for numbers and facts: an app count, a price, what an
 * app's permissions let it do, and a language's name.
 */
import type { StorePricing } from './types';

export function appCount(n: number): string {
  return n === 1 ? '1 app' : `${n} apps`;
}

/** What an app costs to open, in a word or two. */
export function priceLabel(pricing: StorePricing | null): string {
  if (!pricing) return 'Free';
  if (pricing.model === 'fixed' && pricing.priceCents && pricing.priceCents > 0) {
    return `$${(pricing.priceCents / 100).toFixed(2)}`;
  }
  if (pricing.model === 'no_base_cost' || pricing.model === 'per_use') return 'Pay as you go';
  return 'Free';
}

/**
 * What a permission lets an app do, in a person's words. Several scopes say
 * the same thing; a scope with nothing to tell a visitor (the theme) says
 * nothing. Order follows what people most want to know.
 */
const PERMISSION_WORDS: ReadonlyArray<[RegExp, string]> = [
  [/^entities:(invoke|client_invoke)$/, 'Uses AI — billed to you as you use it'],
  [/^entities:image_(generate|edit)$/, 'Makes and edits images with AI'],
  [/^entities:audio_generate$/, 'Makes speech, music and sound with AI'],
  [/^entities:web_search$/, 'Searches the web for you'],
  [/^web:sources:read$/, 'Reads facts from websites Fias approved'],
  [/^vault:(user-documents|documents):read$/, 'Opens files you choose'],
  [/^vault:(user-documents|documents):(write|edit)$/, 'Saves files to your Documents when you ask'],
  [/^vault:workspace-documents:read$/, "Reads your workspace's documents, once its owners approve"],
  [/^vault:arche-saves:write$/, 'Delivers files to your Arche Saves'],
  [/^vault:creative:/, 'Uses your saved pictures and sounds'],
  [/^user:profile:read$/, 'Sees your name and picture'],
  [/^data:workspace-people:read$/, 'Sees who is in your workspace'],
  [/^data:(store|search|workspace|attested|agents)$/, 'Keeps its own data for you'],
  [/^storage:(sandbox|local)$/, 'Keeps its own data for you'],
  [/^store:purchase$/, 'Offers in-app purchases'],
  [/^email:company:send$/, 'Sends email for your Company'],
  [/^connections:use$/, 'Uses accounts you connect'],
  [/^schedule:invoke$/, 'Runs tasks on a schedule you set'],
  [/^ai:actions$/, 'Works with Fias AI'],
  [/^docsign:/, 'Works with your signed documents'],
  [/^navigation:open_arche$/, 'Opens other apps'],
];

export function permissionsInWords(scopes: readonly string[]): string[] {
  const words: string[] = [];
  for (const [pattern, phrase] of PERMISSION_WORDS) {
    if (scopes.some((s) => pattern.test(s)) && !words.includes(phrase)) words.push(phrase);
  }
  return words;
}

/** A language tag's name in English ("pt-BR" → "Portuguese (Brazil)"), or the tag itself. */
export function languageName(tag: string): string {
  try {
    const names = new Intl.DisplayNames(['en'], { type: 'language' });
    return names.of(tag) ?? tag;
  } catch {
    return tag;
  }
}

/** A date as "October 30, 2026", in UTC so a page reads the same everywhere. */
export function formatStoreDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Today's date line on the Today tab: "Friday, October 30". */
export function todayLine(now: number): string {
  if (!now) return '';
  return new Date(now).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
